import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['helpfulButton', 'notHelpfulButton', 'feedbackContainer', 'thanksMessage', 'voteCount']
  static values = {
    articleId: String,
    url: String,
    voted: { type: Boolean, default: false },
    csrfToken: String
  }

  connect() {
    this.checkExistingVote()
  }

  checkExistingVote() {
    const voteKey = this.getVoteKey()
    const existingVote = localStorage.getItem(voteKey)
    
    if (existingVote) {
      this.votedValue = true
      this.updateUIForVote(existingVote === 'helpful')
    }
  }

  async voteHelpful(event) {
    event.preventDefault()
    if (this.votedValue) return
    
    await this.submitVote(true)
  }

  async voteNotHelpful(event) {
    event.preventDefault()
    if (this.votedValue) return
    
    await this.submitVote(false)
  }

  async submitVote(helpful) {
    if (this.votedValue) {
      this.showAlreadyVotedMessage()
      return
    }

    const button = helpful ? this.helpfulButtonTarget : this.notHelpfulButtonTarget
    button.disabled = true
    
    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          article_id: this.articleIdValue,
          helpful: helpful
        })
      })

      if (!response.ok) throw new Error('Vote submission failed')

      const data = await response.json()
      
      this.votedValue = true
      this.storeVote(helpful)
      this.updateUIForVote(helpful)
      this.updateVoteCount(data.vote_count)
      this.showThankYouMessage()
      
    } catch (error) {
      console.error('Error submitting vote:', error)
      this.showErrorMessage()
      button.disabled = false
    }
  }

  storeVote(helpful) {
    const voteKey = this.getVoteKey()
    localStorage.setItem(voteKey, helpful ? 'helpful' : 'not_helpful')
  }

  getVoteKey() {
    return `article_feedback_${this.articleIdValue}`
  }

  updateUIForVote(helpful) {
    if (helpful) {
      this.helpfulButtonTarget.classList.add('bg-green-500', 'text-white')
      this.helpfulButtonTarget.classList.remove('bg-gray-200', 'hover:bg-gray-300')
      this.notHelpfulButtonTarget.disabled = true
      this.notHelpfulButtonTarget.classList.add('opacity-50')
    } else {
      this.notHelpfulButtonTarget.classList.add('bg-red-500', 'text-white')
      this.notHelpfulButtonTarget.classList.remove('bg-gray-200', 'hover:bg-gray-300')
      this.helpfulButtonTarget.disabled = true
      this.helpfulButtonTarget.classList.add('opacity-50')
    }
    
    this.helpfulButtonTarget.disabled = true
    this.notHelpfulButtonTarget.disabled = true
  }

  updateVoteCount(count) {
    if (this.hasVoteCountTarget && count !== undefined) {
      this.voteCountTarget.textContent = `${count} ${count === 1 ? 'person' : 'people'} found this helpful`
    }
  }

  showThankYouMessage() {
    if (this.hasThanksMessageTarget) {
      this.thanksMessageTarget.classList.remove('hidden')
      this.thanksMessageTarget.textContent = 'Thank you for your feedback!'
      
      setTimeout(() => {
        this.fadeOutMessage()
      }, 3000)
    }
  }

  showAlreadyVotedMessage() {
    if (this.hasThanksMessageTarget) {
      this.thanksMessageTarget.classList.remove('hidden')
      this.thanksMessageTarget.textContent = 'You have already voted on this article'
      this.thanksMessageTarget.classList.add('text-yellow-600')
      
      setTimeout(() => {
        this.fadeOutMessage()
      }, 3000)
    }
  }

  showErrorMessage() {
    if (this.hasThanksMessageTarget) {
      this.thanksMessageTarget.classList.remove('hidden')
      this.thanksMessageTarget.textContent = 'Sorry, there was an error submitting your vote. Please try again.'
      this.thanksMessageTarget.classList.add('text-red-600')
      
      setTimeout(() => {
        this.fadeOutMessage()
      }, 5000)
    }
  }

  fadeOutMessage() {
    if (this.hasThanksMessageTarget) {
      this.thanksMessageTarget.classList.add('opacity-0', 'transition-opacity', 'duration-500')
      
      setTimeout(() => {
        this.thanksMessageTarget.classList.add('hidden')
        this.thanksMessageTarget.classList.remove('opacity-0', 'text-yellow-600', 'text-red-600')
      }, 500)
    }
  }

  reset() {
    const voteKey = this.getVoteKey()
    localStorage.removeItem(voteKey)
    
    this.votedValue = false
    
    this.helpfulButtonTarget.disabled = false
    this.notHelpfulButtonTarget.disabled = false
    
    this.helpfulButtonTarget.classList.remove('bg-green-500', 'text-white', 'opacity-50')
    this.notHelpfulButtonTarget.classList.remove('bg-red-500', 'text-white', 'opacity-50')
    
    this.helpfulButtonTarget.classList.add('bg-gray-200', 'hover:bg-gray-300')
    this.notHelpfulButtonTarget.classList.add('bg-gray-200', 'hover:bg-gray-300')
    
    if (this.hasThanksMessageTarget) {
      this.thanksMessageTarget.classList.add('hidden')
    }
  }
}